import React, { useState } from 'react';
import { Modal, Radio } from 'antd';
import { EntityListTypeName, MissingSkuHandling } from '@ydistri/api-sdk';
import { Column, Text } from '@ydistri/ds';
import { formatNumber } from '@ydistri/generic-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../store';
import { useGetEntityListImportSummaryQuery } from '../ProjectAdministration/EntityListAdministration/apiEntityLists';
import { useFinalizeEntityListImport } from '../ProjectAdministration/EntityListAdministration/hooks/useFinalizeEntityListImport';
import { AvailableModals, closeModal } from './modalSlice';

const EntityListSkuHandlingModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const data = useSelector(
    (state: ReduxState) => state.modal.modalData[AvailableModals.ENTITY_LIST_SKU_HANDLING],
  );
  const {
    data: summary,
    isFetching,
    isLoading,
    isError,
  } = useGetEntityListImportSummaryQuery(data?.entityListId ?? skipToken);
  const { finalize, pending } = useFinalizeEntityListImport(
    data?.entityListType ?? EntityListTypeName.Product,
  );
  const [choice, setChoice] = useState<{ handling: MissingSkuHandling; token: string }>();
  const ready = !isLoading && !isFetching && !isError && !!summary?.ignoreValidationToken;
  const selected = choice?.token === summary?.ignoreValidationToken ? choice?.handling : undefined;
  const productList = data?.entityListType === EntityListTypeName.Product;

  if (!data) return null;

  const confirm = async () => {
    if (!ready || pending || selected === undefined) return;
    const result = await finalize({
      entityListId: data.entityListId,
      verificationTimeStamp: data.verificationTimeStamp,
      missingSkuHandling: selected,
      ignoreValidationToken: summary.ignoreValidationToken,
    });
    if (result !== 'failed') dispatch(closeModal());
  };

  return (
    <Modal
      title="Handling of not active SKUs"
      centered
      open={true}
      onOk={() => void confirm()}
      okText="Confirm"
      confirmLoading={pending}
      okButtonProps={{ disabled: !ready || pending || selected === undefined }}
      cancelButtonProps={{ disabled: pending }}
      closable={!pending}
      maskClosable={!pending}
      keyboard={!pending}
      onCancel={() => {
        if (!pending) dispatch(closeModal());
      }}
    >
      <Column $gap="1rem">
        {summary && (
          <>
            <Text>
              {productList
                ? 'Not active SKUs counts products without any enabled SKU in current attributes. A product stays if at least one of its SKUs qualifies.'
                : 'Not active SKUs includes missing SKUs and SKUs whose current attributes are missing or disabled.'}
            </Text>
            <Text>
              Ignore will remove {formatNumber(summary.totalNotActiveItems)}{' '}
              {productList ? 'products' : 'items'}, including retained items from append imports.
              The resulting list will contain {formatNumber(summary.totalItemsAfterIgnore)}{' '}
              {productList ? 'products' : 'items'}.
            </Text>
            <Text>Removed items will not be prepared or reactivated through this import.</Text>
          </>
        )}
        {!ready && (
          <Text $type={isError ? 'danger' : undefined}>
            {isError
              ? 'Validation could not be loaded. Close this dialog and retry validation.'
              : 'Refreshing validation…'}
          </Text>
        )}
        <Radio.Group
          value={selected ?? null}
          disabled={!ready || pending}
          onChange={event => {
            if (!summary || !ready) return;
            setChoice({
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Radio values are the handling enum
              handling: event.target.value as MissingSkuHandling,
              token: summary.ignoreValidationToken,
            });
          }}
        >
          <Column $gap="0.5rem">
            <Radio value={MissingSkuHandling.Ignore}>Ignore</Radio>
            <Radio value={MissingSkuHandling.AddAndEnable}>
              {productList
                ? 'Keep products and prepare eligible existing SKUs'
                : 'Add missing and enable disabled SKUs'}
            </Radio>
          </Column>
        </Radio.Group>
        <Text>
          {productList
            ? 'Existing enabled SKUs are prepared where supported by the current configuration. This does not create SKU combinations or enable every SKU of a product.'
            : 'Existing enabled SKUs with missing or disabled attributes are prepared where supported by the current configuration.'}{' '}
          Items outside that coverage stay in the list without preparation.
        </Text>
      </Column>
    </Modal>
  );
};

export default EntityListSkuHandlingModal;
